import React, { useEffect, useState, useRef, useCallback } from "react";
import Input from "../../components/controls/input/customInput/CustomInput";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import InputUnderline from "../../components/controls/input/InputUnderline";
import { CalendarIcon } from "../../modules/default-svg";
import Searchable from "../../components/controls/select/CustomSelect";
import {
  calculatePageSize,
  getLabelByType,
  MilestonePriorityOptions,
  MilestoneStatusOptions,
} from "../../helpers/utils";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import Table from "../../components/controls/tables/Table";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import AssociatedRisks from "../risk/AssociatedRisks";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import { Skeleton } from "@fluentui/react-components";
import searchService from "../../store/services/search.service";

const Milestone = (props) => {

  const {
    parentData,
    parentType,
    milestones,
    pagination,
    currentMilestone,
    loading,
    actionLoading,
    milestoneLoading,
    error,
    getMilestonesRequest,
    getMilestoneRequest,
    createMilestoneRequest,
    updateMilestoneRequest,
    deleteMilestoneRequest,
    resetMilestoneErrorsRequest,
    clearCurrentMilestoneRequest
  } = props;


  const [data, setData] = useState([]);
  const [perPageItems] = useState(calculatePageSize());
  const [pageNum, setPageNum] = useState(1);
  const [selectedRow, setSelectedRow] = useState({});
  const [isCreate, setIsCreate] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isRichtextContentReady, setIsRichtextContentReady] = useState(false);
  const [tableSearch, setTableSearch] = useState(null);
  const [formErrors, setFormErrors] = useState({
    title: "",
    project: "",
  });

  // Form Fields
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState(0);
  const [status, setStatus] = useState(0);
  const [priority, setPriority] = useState(1);
  const [targetDate, setTargetDate] = useState(null);
  const [description, setDescription] = useState("");
  const [linkedDescription, setLinkedDescription] = useState("");
  const [scope, setScope] = useState(null);
  const [task, setTask] = useState(null);
  const [isLinked, setIsLinked] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [risks, setRisks] = useState([]);

  const [programsOptions, setProgramsOptions] = useState([]);
  const [projectsOptions, setProjectsOptions] = useState([]);

  // Context
  const [project, setProject] = useState(parentType == "Project" ? { id: parentData.id, name: parentData.name } : null);
  const [program, setProgram] = useState(parentType == "Program" ? { id: parentData.id, name: parentData.name } : null);


  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = {
      parent: parentType,
      page: pageNumber ?? pageNum,
      size: pageSize ?? perPageItems,
      search: forceClearSearch ? null : (searchTerm ?? tableSearch)
    };
    switch (parentType) {
      case "Portfolio":
        payload.id = parentData.id;
        break;
      case "Program":
        payload.id = parentData.id;
        break;
      case "Project":
        payload.id = parentData.id;
        break;
      default:
        return;
    }
    getMilestonesRequest(payload);
  };

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);


  const handleSave = () => {

    const errors = {};
    if (!title) errors["title"] = `The Title field is required.`;

    if (!project) errors["project"] = `The Project field is required.`;

    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }

    const payload = {
      Title: title,
      Budget: budget,
      Status: status,
      Priority: priority,
      TargetDate: targetDate,
      Description: description,
    };

    if (Object.keys(selectedRow).length > 0) {
      updateMilestoneRequest({
        id: selectedRow.id,
        data: payload,
        callback: onMilestoneActionCompleted,
      });
    } else {

      payload.ProjectId = project.id;
      createMilestoneRequest({
        data: payload,
        callback: onMilestoneActionCompleted,
      });
    }
  };

  const onMilestoneActionCompleted = () => {
    fetchData();
    resetMilestone();
  };

  const resetMilestone = () => {
    setProject(parentType == "Project" ? { id: parentData.id, name: parentData.name } : null);
    setProgram(parentType == "Program" ? { id: parentData.id, name: parentData.name } : null);
    setTitle("");
    setBudget(0);
    setStatus(0);
    setPriority(1);
    setTargetDate(null);
    setDescription("");
    setLinkedDescription("");
    setScope(null);
    setTask(null);
    setIsLinked(false);
    setAttachments([]);
    setRisks([]);
    setIsRichtextContentReady(false);

    clearCurrentMilestoneRequest();
    resetMilestoneErrorsRequest();

    setFormErrors({});
    setIsEdit(false);
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      isEdit && getMilestoneRequest(selectedRow.id);
    } else {
      resetMilestone();
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    if (currentMilestone && isEdit && !milestoneLoading) {
      setTitle(currentMilestone.title);
      setBudget(currentMilestone.budget);
      setStatus(currentMilestone.status);
      setPriority(currentMilestone.priority);
      setTargetDate(currentMilestone.targetDate);
      setDescription(currentMilestone.description);
      setLinkedDescription(currentMilestone.linkedDescription);
      setProject(currentMilestone.project);
      setScope(currentMilestone.scope);
      setTask(currentMilestone.task);
      setIsLinked(currentMilestone.isLinked || false);
      setAttachments(currentMilestone.attachments || []);
      setRisks(currentMilestone.risks || []);
      setIsRichtextContentReady(true);
    }
  }, [currentMilestone, isEdit, milestoneLoading]);


  const handleDelete = (selectedRow) => {

    const id = selectedRow.id;
    deleteMilestoneRequest({
      id: id,
      callback: () => {
        setDeletedId(id);
        onMilestoneActionCompleted();
      },
    });
  };

  const handleCancel = () => {
    resetMilestone();
  };

  useEffect(() => {
    if (milestones && milestones.length > 0) {
      if (pageNum === 1) {
        setData(
          milestones
            .filter((item) => item.id !== deleteId)
            .sort((a, b) => {
              return b.isFlagged - a.isFlagged;
            })
        );
      }
      else {
        setData((prevData) => {
          const newMap = new Map(milestones.map((item) => [item.id, item]));

          const updatedPrevData = prevData.map((item) =>
            newMap.has(item.id) ? newMap.get(item.id) : item
          );

          const prevDataIds = new Set(prevData.map((item) => item.id));
          const newItems = milestones.filter(
            (milestone) => !prevDataIds.has(milestone.id)
          );

          return [...updatedPrevData, ...newItems]
            .filter((item) => item.id !== deleteId)
            .sort((a, b) => b.isFlagged - a.isFlagged);
        });
      }

    } else {
      setData([]);
      setIsEdit(false);
    }
  }, [milestones]);

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  const searchProgramHandler = (value) => {

    if (parentType !== "Portfolio") return;

    searchService
      .Programs(value, { portfolioId: parentData.id })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setProgramsOptions(transformedArray);
      })
      .catch(() => {
        setProgramsOptions([]);
      });
  };

  const searchProjectHandler = (value) => {

    if (parentType !== "Portfolio" && parentType !== "Program") return;

    let payload = {};

    if (parentType === "Portfolio") {
      payload.portfolioId = parentData.id;
    }
    if (program) {
      payload.programId = program.id;
    }

    searchService
      .Projects(value, payload)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setProjectsOptions(transformedArray);
      })
      .catch(() => {
        setProjectsOptions([]);
      });
  };

  const formBody = () => {
    return (
      <div>
        {
          !isEdit && parentType !== "Project" && (
            <div className="row align-items-center gx-3 mb-4">
              {
                parentType === "Portfolio" &&
                <div className="col-6 w-50">
                  <Searchable
                    label="Program"
                    SelectText="Program"
                    searchText={searchProgramHandler}
                    options={programsOptions}
                    defaultVal={program ? { value: program.id, label: program.name } : null}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setProgram({ id: selectedOption.value, name: selectedOption.label });
                      } else {
                        setProgram(null);
                      }
                    }}
                  />
                </div>
              }

              <div className="col-6 w-50">
                <Searchable
                  label="Project"
                  SelectText="Project"
                  searchText={searchProjectHandler}
                  options={projectsOptions}
                  defaultVal={project ? { value: project.id, label: project.name } : null}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setProject({ id: selectedOption.value, name: selectedOption.label });
                    } else {
                      setProject(null);
                    }
                  }}
                  errorMessage={formErrors.project}
                  isRequired
                />
              </div>
            </div>
          )
        }
        <div className="row align-items-end gx-3 mb-3">
          <div className={"col-9"}>
            {
              isEdit && isLinked ?
                (
                  <div>
                    {
                      scope &&
                      <div>
                        <label class="Input_label">Scope</label>
                        {milestoneLoading ? (
                          <Skeleton
                            height={40}
                            baseColor="#e0e0e0"
                            highlightColor="#ECE7FD"
                          />
                        ) : (
                          <p
                            style={{ border: "1px solid #342C82" }}
                            className={`d-flex items-center justify-star px-[8px] text-[#1F1F1F] bg-[#EDECF8] text-center py-[4px] rounded-md h-[40px]`}
                          >
                            {`${scope.name}`}
                          </p>
                        )}
                      </div>
                    }
                    {
                      task &&
                      <div className="mt-3">
                        <label class="Input_label">Task</label>
                        {milestoneLoading ? (
                          <Skeleton
                            height={40}
                            baseColor="#e0e0e0"
                            highlightColor="#ECE7FD"
                          />
                        ) : (
                          <p
                            style={{ border: "1px solid #342C82" }}
                            className={`d-flex items-center justify-star px-[8px] text-[#1F1F1F] bg-[#EDECF8] text-center py-[4px] rounded-md h-[40px]`}
                          >
                            {`${task.name}`}
                          </p>
                        )}
                      </div>
                    }
                  </div>
                )
                :
                <InputUnderline
                  placeholder="Add title"
                  value={title}
                  handleChange={setTitle}
                  error={formErrors.title}
                  loading={milestoneLoading}
                  isRequired
                />
            }

          </div>
          <div className="col-3">
            <Input
              icon={<CalendarIcon />}
              label="Target Date"
              type="date"
              value={targetDate}
              handleChange={setTargetDate}
              placeholder="Enter Target Date"
              loading={milestoneLoading}
              isDisabled={isLinked}
            />
          </div>

        </div>

        {isEdit && <AssociatedRisks isLoading={milestoneLoading} risks={risks} />}
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              value={budget}
              type="number"
              currency={true}
              placeholder="0"
              handleChange={setBudget}
              label="Budget"
              loading={milestoneLoading}
              isDisabled={isLinked}
            />
          </div>
          <div className="col-4">
            <DropDown
              label="Status"
              options={MilestoneStatusOptions}
              defaultVal={getLabelByType("milestoneStatus", status)}
              onChange={setStatus}
              loading={milestoneLoading}
              disabled={isLinked}
            />
          </div>
          <div className="col-4">
            <DropDown
              label="Priority"
              options={MilestonePriorityOptions}
              defaultVal={getLabelByType("milestonePriority", priority)}
              onChange={setPriority}
              loading={milestoneLoading}
              disabled={isLinked}
            />
          </div>
        </div>
        {
          isLinked &&
          <div className="mb-3">
            <RichTextEditor
              label={`${task ? "Task" : "Scope"} Description`}
              content={linkedDescription}
              loading={currentMilestone && !isRichtextContentReady}
              disabled={true}
            />
          </div>
        }

        <div className="mb-3">
          <RichTextEditor
            label="Comments"
            content={description}
            onChange={setDescription}
            loading={currentMilestone && !isRichtextContentReady}
          />
        </div>


        {isEdit && (!isLinked || attachments && attachments.length > 0) &&
          <EntityFileUpload
            entityId={selectedRow.id}
            entityType="Milestone"
            attachments={attachments}
            onAttachmentsChange={() => {
              if (selectedRow.id) {
                getMilestoneRequest(selectedRow.id);
              }
            }}
            disabled={milestoneLoading || !selectedRow.id || isLinked}
          />
        }

      </div>
    );
  };

  useEffect(() => {
    if (title) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        title: "",
      }));
    }
    if (project) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        project: "",
      }));
    }
  }, [project, title]);


  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <ColumnConfigProvider
      tableName={`${parentType}-Milestone`}
    >
      <div className="listingScreen position-relative">
        <Table
          type="Milestone"
          parentType={parentType}
          rows={data || []}
          searchHandler={handleSearch}
          body={formBody()}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={setIsCreate}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          loading={loading}
          modalLoading={actionLoading}
          isCreate={isCreate}
          pagination={pagination}
          setSelectedRow={setSelectedRow}
          projectId={parentType == "Project" ? parentData.id : null}
          programId={parentType == "Program" ? parentData.id : null}
          portfolioId={parentType == "Portfolio" ? parentData.id : null}
          setData={setData}
          triggerSearch={handleSearchTrigger}
          isModalSaveBtnDisabled={false}
        />
      </div>
    </ColumnConfigProvider>
  );
};
export default Milestone;
